"use client";

import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";

// Sticky tab bar at the bottom of the screen. Shows while the feature
// sections are on screen, and highlights the section being read.
export function SectionTabs({ groups }) {
  const [activeId, setActiveId] = useState(groups[0]?.id);
  const [visible, setVisible] = useState(false);
  const tabRefs = useRef({});
  const navRef = useRef(null);

  useEffect(() => {
    let frame = null;

    const update = () => {
      frame = null;
      const sections = groups.map((group) => document.getElementById(group.id)).filter(Boolean);
      if (!sections.length) return;

      const viewport = window.innerHeight;
      const first = sections[0].getBoundingClientRect();
      const last = sections[sections.length - 1].getBoundingClientRect();

      // Show from when the first section fills most of the screen until the last one leaves
      setVisible(first.top < viewport * 0.6 && last.bottom > viewport * 0.5);

      // Active = the last section whose top has passed 40% of the screen
      let current = sections[0].id;
      for (const section of sections) {
        if (section.getBoundingClientRect().top <= viewport * 0.4) current = section.id;
      }
      setActiveId(current);
    };

    const onScroll = () => {
      if (frame == null) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame != null) cancelAnimationFrame(frame);
    };
  }, [groups]);

  // On phones the bar scrolls sideways, so keep the active tab in view
  useEffect(() => {
    const nav = navRef.current;
    const tab = tabRefs.current[activeId];
    if (!nav || !tab || nav.scrollWidth <= nav.clientWidth) return;
    nav.scrollTo({ left: tab.offsetLeft - (nav.clientWidth - tab.offsetWidth) / 2, behavior: "smooth" });
  }, [activeId]);

  const goTo = (event, id) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      aria-hidden={!visible}
      className={cn(
        "pointer-events-none fixed inset-x-0 bottom-4 z-40 px-4 transition-all duration-300 md:bottom-6",
        visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
      )}
    >
      <nav
        ref={navRef}
        aria-label="Feature sections"
        className={cn(
          "mx-auto flex max-w-7xl overflow-x-auto rounded-full bg-[#11110d] p-1 shadow-[0_12px_30px_rgba(17,17,13,0.25)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          visible && "pointer-events-auto",
        )}
      >
        {groups.map((group) => {
          const isActive = group.id === activeId;

          return (
            <a
              key={group.id}
              ref={(node) => {
                tabRefs.current[group.id] = node;
              }}
              href={`#${group.id}`}
              tabIndex={visible ? 0 : -1}
              aria-current={isActive ? "true" : undefined}
              onClick={(event) => goTo(event, group.id)}
              className={cn(
                "relative flex-1 shrink-0 whitespace-nowrap rounded-full px-5 py-2.5 text-center text-sm transition-colors md:py-3",
                isActive ? "text-neutral-900" : "text-white/70 hover:text-white",
              )}
            >
              {isActive && (
                <motion.span
                  layoutId="features-active-tab"
                  transition={{ type: "spring", stiffness: 400, damping: 35 }}
                  className="absolute inset-0 rounded-full bg-white shadow-sm"
                />
              )}
              <span className="relative">{group.label}</span>
            </a>
          );
        })}
      </nav>
    </div>
  );
}
